import { Line } from 'react-chartjs-2';
import moment from 'moment';
import { useMemo } from 'react';
import { ChartData, ChartOptions, CoreChartOptions } from 'chart.js';
import color from '@deprecated-ui/_style/color';
import { CompanyChartData } from 'lib/entities/company.types';

type CompanyChartProps = {
  chart: CompanyChartData;
  onClickChart?: CoreChartOptions<'line'>['onClick'];
};

const fillColor = color.clearDay;
const lineColor = color.mountainMeadow;

function CompanyChart({ chart, onClickChart }: CompanyChartProps) {
  const { prices = [], timeframe } = chart ?? {};

  const createPreviousMonth = (numberOfMonth: number) => {
    const data = [];
    const dateStart = moment().subtract(numberOfMonth, 'month');
    const dateEnd = moment();
    while (dateStart.diff(dateEnd, 'months') <= 0) {
      data.push(dateStart.format('MMM YYYY'));
      dateStart.add(1, 'month');
    }
    return data;
  };

  const createPreviousYear = (numberOfYear: number) => {
    const data = [];
    const dateStart = moment().subtract(numberOfYear, 'y');
    const dateEnd = moment();
    while (dateStart.diff(dateEnd, 'years') <= 0) {
      data.push(dateStart.format('YYYY'));
      dateStart.add(1, 'year');
    }
    return data;
  };

  const [format, maxTicks, labels, autoSkip = true] = useMemo(() => {
    if (timeframe === '1w') return ['DD MMM', 5, []];
    if (timeframe === '1m') return ['DD MMM', 5, []];
    if (timeframe === '3m') {
      const previousMonths = createPreviousMonth(2);
      return ['MMM', 3, previousMonths];
    }
    if (timeframe === 'ytd') return ['DD MMM', 5];
    if (timeframe === '1y') {
      const previousMonths = createPreviousMonth(11);
      return ['MMM', 12, previousMonths, false];
    }
    if (timeframe === '3y') {
      const previousYear = createPreviousYear(2);
      return ['YYYY', 3, previousYear];
    }
    if (timeframe === '5y') {
      const previousYear = createPreviousYear(4);
      return ['YYYY', 5, previousYear];
    }

    return ['HH:mm', 6, []];
  }, [timeframe]);

  const tempLabels = [];
  const chartData: ChartData<'line'> = useMemo(
    () => ({
      labels: prices.map((price) => {
        if (!price.formatted_date && price.date === '0') return '';

        const formattedDate = moment(price.formatted_date);
        const monthYear = formattedDate.format('MMM YYYY');
        const month = formattedDate.format('MMM');
        const year = formattedDate.format('YYYY');
        const formattedByTimeframe = formattedDate.format(format);
        switch (timeframe) {
          case '1w':
            if (formattedDate.hour() <= 9 && !tempLabels.includes(formattedByTimeframe)) {
              tempLabels.push(formattedByTimeframe);
              return formattedByTimeframe;
            }

            return '';
          case '1m':
          case 'today':
            return formattedDate.minute() === 0
              ? formattedByTimeframe
              : '';
          case '3m':
          case '1y':
            if (labels.includes(monthYear)) {
              labels.splice(labels.indexOf(monthYear), 1);
              return month;
            }
            return '';
          case '3y':
          case '5y':
            if (labels.includes(year)) {
              labels.splice(labels.indexOf(year), 1);
              return year;
            }
            return '';
          default:
            return moment(price.formatted_date).format(format);
        }
      }),
      datasets: [
        {
          fill: true,
          tension: 0,
          backgroundColor: fillColor,
          borderColor: lineColor,
          borderCapStyle: 'butt',
          borderDashOffset: 0.0,
          borderJoinStyle: 'round',
          borderWidth: 2,
          data: prices.map((pr) => parseInt(pr.value, 10)),
        },
      ],
    }),
    [prices, format],
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      animation: {
        duration: 0,
      },
      resizeDelay: 0, // animation duration after a resize
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      maintainAspectRatio: false,
      scales: {
        y: {
          position: 'right',
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            maxTicksLimit: 8,
            callback(val) {
              let price = val;
              // if price contain decimal, then hide tick
              if (typeof price === 'number' && price % 1 !== 0) price = null;
              return price || null;
            },
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            align: 'start',
            maxTicksLimit: maxTicks,
            maxRotation: 0,
            callback(val) {
              // eslint-disable-next-line react/no-this-in-sfc
              return this.getLabelForValue(val) || null;
            },
            autoSkip,
            precision: 0,
          },
        },
      },
      elements: {
        point: {
          radius: 0,
        },
      },
      events: ['click'],
      onClick: onClickChart,
    }),
    [maxTicks, onClickChart],
  );

  return <Line data={chartData} options={options} height={300} />;
}

export default CompanyChart;
